// ============================================================
//  src/pages/AdminPanel.jsx
//
//  Tabs: 📋 Orders · 📦 Subscriptions · 💰 Prices · 🏷️ Stock

import { useState, useEffect, useRef } from 'react';
import baseProducts from '../data/products';
import { fetchOverrides, saveOverride, deleteOverride, resetAllOverrides } from '../hooks/useProducts';
import { supabase } from '../utils/supabase';
import config from '../config';
import { useNavigate } from "react-router-dom";



// ── Order status config ──────────────────────────────────────
const ORDER_STATUSES = [
  { key: 'pending',   label: '⏳ Pending',   bg: '#fff7ed', color: '#c2410c', next: 'confirmed' },
  { key: 'confirmed', label: '✅ Confirmed', bg: '#f0fdf4', color: '#15803d', next: 'shipped'   },
  { key: 'shipped',   label: '🚚 Shipped',   bg: '#eff6ff', color: '#1d4ed8', next: 'delivered' },
  { key: 'delivered', label: '📦 Delivered', bg: '#f5f3ff', color: '#6d28d9', next: null        },
  { key: 'cancelled', label: '❌ Cancelled', bg: '#fef2f2', color: '#dc2626', next: null        },
];

// ── Subscription status config ───────────────────────────────
const SUB_STATUSES = [
  { key: 'pending',   label: '⏳ Pending',   bg: '#fff7ed', color: '#c2410c' },
  { key: 'active',    label: '✅ Active',    bg: '#f0fdf4', color: '#15803d' },
  { key: 'paused',    label: '⏸ Paused',    bg: '#eff6ff', color: '#1d4ed8' },
  { key: 'cancelled', label: '❌ Cancelled', bg: '#fef2f2', color: '#dc2626' },
];

// ── Stock status config ──────────────────────────────────────
const STOCK_STATUSES = [
  { key: 'in_stock',     label: '✅ In Stock',     bg: '#f0fdf4', color: '#15803d' },
  { key: 'low_stock',    label: '⚠️ Low Stock',    bg: '#fff7ed', color: '#c2410c' },
  { key: 'out_of_stock', label: '❌ Out of Stock', bg: '#fef2f2', color: '#dc2626' },
];

const PLAN_META = {
  starter:  { icon: '🌾', label: 'Starter Box',  color: '#2d6a4f' },
  family:   { icon: '🏡', label: 'Family Box',   color: '#15803d' },
  wellness: { icon: '✨', label: 'Wellness Box', color: '#6d28d9' },
};

const FREQ_LABEL = {
  monthly:   'Monthly',
  quarterly: 'Every 3 Months',
  biannual:  'Every 6 Months',
};

function getStatus(key) {
  return ORDER_STATUSES.find((s) => s.key === key) || ORDER_STATUSES[0];
}
function getSubStatus(key) {
  return SUB_STATUSES.find((s) => s.key === key) || SUB_STATUSES[0];
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
function fmtDateOnly(iso) {
  if (!iso) return '—';
  // Append T00:00:00 to treat as LOCAL time, avoiding UTC midnight → previous day shift
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Days until next dispatch ──────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  // Compare date-only (no time) to avoid timezone drift
  const target = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function DispatchBadge({ dateStr }) {
  if (!dateStr) return <span style={{ color: '#bbb', fontSize: 11 }}>Not set</span>;
  const days = daysUntil(dateStr);
  const overdue  = days < 0;
  const urgent   = days >= 0 && days <= 2;
  const soon     = days > 2  && days <= 7;
  const bg    = overdue ? '#fef2f2' : urgent ? '#fff7ed' : soon ? '#fefce8' : '#f0fdf4';
  const color = overdue ? '#dc2626' : urgent ? '#c2410c' : soon ? '#854d0e' : '#15803d';
  const label = overdue
    ? `⚠️ ${Math.abs(days)}d overdue`
    : days === 0 ? '🔴 Today!'
    : days === 1 ? '🟠 Tomorrow'
    : `${days}d away`;
  return (
    <span style={{ background: bg, color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── Price manager helpers ────────────────────────────────────
function buildDraft(overrides) {
  return baseProducts.map((p) => ({
    id:             p.id,
    name:           p.name,
    emoji:          p.emoji,
    category:       p.category,
    sizes:          Object.keys(p.prices),
    prices:         overrides[p.id] ? { ...overrides[p.id] } : { ...p.prices },
    originalPrices: { ...p.prices },
    savedPrices:    overrides[p.id] ? { ...overrides[p.id] } : null,
    expanded:       false,
  }));
}
function isDirty(draft) {
  const base = draft.savedPrices || draft.originalPrices;
  return Object.keys(draft.prices).some((s) => Number(draft.prices[s]) !== Number(base[s]));
}

// ════════════════════════════════════════════════════════════
//  LOGIN SCREEN
// ════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const emailRef                = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  async function attempt(e) {
    e.preventDefault();
    if (!email.trim() || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true); setError('');
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (authError) { setError('Invalid email or password.'); return; }
      const { data: adminRow, error: adminError } = await supabase.from('admins').select('id').eq('id', authData.user.id).maybeSingle();
      if (adminError || !adminRow) {
        await supabase.auth.signOut();
        setError('Access denied. This account does not have admin privileges.');
        return;
      }
      onLogin(authData.user);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  }

  return (
    <div style={S.loginWrap}>
      <div style={S.loginCard}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 38, marginBottom: 8 }}>🌾</div>
          <h1 style={S.loginTitle}>RK Farm Foods</h1>
          <p style={S.loginSub}>Admin Panel · Sign in to continue</p>
        </div>
        <form onSubmit={attempt} style={{ width: '100%' }}>
          <div style={S.fieldWrap}>
            <label style={S.fieldLabel}>Email</label>
            <input ref={emailRef} type="email" value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="admin@example.com" style={S.input} autoComplete="email" />
          </div>
          <div style={S.fieldWrap}>
            <label style={S.fieldLabel}>Password</label>
            <input type="password" value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="••••••••" style={S.input} autoComplete="current-password" />
          </div>
          {error && <div style={S.loginError}>⚠️ {error}</div>}
          <button type="submit" disabled={loading}
            style={{ ...S.loginBtn, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Signing in...' : 'Sign in →'}
          </button>
        </form>
        <p style={{ fontSize: 11, color: '#ccc', textAlign: 'center', marginTop: 20, lineHeight: 1.5 }}>
          Access restricted to authorised administrators only.
        </p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  ORDER MANAGEMENT TAB
// ════════════════════════════════════════════════════════════
function StatusBadge({ statusKey }) {
  const s = getStatus(statusKey);
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function OrderCard({ order, onStatusChange, updating }) {
  const [expanded, setExpanded]             = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(order.status || 'pending');
  const items      = order.items   || [];
  const address    = order.address || {};
  const isUpdating = updating === order.id;
  const current    = order.status || 'pending';
  const addrStr    = [address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(', ');

  return (
    <div style={{ ...S.card, marginBottom: 10 }}>
      <div style={{ ...S.cardHead, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>#{String(order.id).padStart(5, '0')}</span>
            <StatusBadge statusKey={current} />
          </div>
          <div style={S.cardMeta}>
            {fmtDate(order.created_at)} &nbsp;·&nbsp;
            <strong style={{ color: config.colors.primary }}>₹{order.total}</strong> &nbsp;·&nbsp;
            {items.length} item{items.length !== 1 ? 's' : ''}
          </div>
        </div>
        <span style={{ ...S.chevron, transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
      </div>

      {expanded && (
        <div style={S.cardBody}>
          {/* Items */}
          <div style={{ background: '#faf8f5', borderRadius: 10, padding: '10px 12px' }}>
            <div style={S.sectionLabel}>Items ordered</div>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ color: '#555' }}>{item.name}{item.size ? ` (${item.size})` : ''} × {item.qty}</span>
                <span style={{ fontWeight: 700 }}>₹{item.subtotal}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px dashed #e4e2dc', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14 }}>
              <span>Total</span><span style={{ color: config.colors.primary }}>₹{order.total}</span>
            </div>
          </div>

          {/* Address */}
          {addrStr && (
            <div>
              <div style={S.sectionLabel}>📦 Delivery address</div>
              <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>{addrStr}</div>
            </div>
          )}

          {/* Status updater */}
          <div style={{ background: '#f9f8f6', border: '0.5px solid #e4e2dc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={S.sectionLabel}>Update order status</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
              {ORDER_STATUSES.filter((s) => s.key !== 'cancelled').map((s, i, arr) => {
                const keys         = arr.map((x) => x.key);
                const currentIndex = keys.indexOf(current);
                const thisIndex    = keys.indexOf(s.key);
                const done   = thisIndex < currentIndex;
                const active = s.key === current;
                return (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: active ? s.bg : done ? '#e8f5e9' : '#f5f5f5', color: active ? s.color : done ? '#2e7d32' : '#ccc', border: active ? `1.5px solid ${s.color}` : '1.5px solid transparent' }}>
                      {s.label}
                    </div>
                    {i < arr.length - 1 && <span style={{ color: '#ddd', fontSize: 12 }}>→</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}
                style={{ flex: 1, minWidth: 140, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', color: '#1a1a1a', outline: 'none' }}>
                {ORDER_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button
                onClick={() => { if (selectedStatus !== current) onStatusChange(order.id, selectedStatus); }}
                disabled={isUpdating || selectedStatus === current}
                style={{ ...S.btnPrimary, padding: '8px 16px', opacity: (isUpdating || selectedStatus === current) ? 0.45 : 1, cursor: (isUpdating || selectedStatus === current) ? 'not-allowed' : 'pointer' }}>
                {isUpdating ? 'Saving...' : 'Update →'}
              </button>
            </div>
            {current !== 'delivered' && current !== 'cancelled' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {getStatus(current).next && (
                  <button onClick={() => onStatusChange(order.id, getStatus(current).next)} disabled={isUpdating}
                    style={{ ...S.btnPrimary, padding: '7px 14px', fontSize: 12, background: config.colors.primary }}>
                    Mark as {getStatus(getStatus(current).next).label} →
                  </button>
                )}
                <button onClick={() => onStatusChange(order.id, 'cancelled')} disabled={isUpdating}
                  style={{ ...S.btnGhost, padding: '7px 14px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }}>
                  Cancel order
                </button>
              </div>
            )}
          </div>

          {/* WhatsApp notify */}
          <button
            onClick={() => {
              const s   = getStatus(current);
              const msg = encodeURIComponent(`Hi! 👋 Your RK Farm Foods order #${String(order.id).padStart(5, '0')} status has been updated to: ${s.label}\n\nOrder total: ₹${order.total}\n${addrStr ? `Delivery to: ${addrStr}\n` : ''}\nThank you for shopping with us! 🌾`);
              window.open(`https://wa.me/${config.whatsappNumber}?text=${msg}`, '_blank');
            }}
            style={{ ...S.btnGhost, width: '100%', background: '#f0fff4', borderColor: '#b2dfdb', color: '#15803d', fontSize: 13 }}>
            💬 Notify Customer on WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter]     = useState('all');
  const [toast, setToast]       = useState(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    loadOrders();
    const channel = supabase
      .channel('admin_orders_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders_table' }, loadOrders)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  async function loadOrders() {
    const { data, error } = await supabase.from('orders_table').select('id, created_at, total, items, address, status').order('created_at', { ascending: false }).limit(200);
    if (error) setError('Failed to load orders: ' + error.message);
    else setOrders(data || []);
    setLoading(false);
  }
  async function handleStatusChange(orderId, newStatus) {
    setUpdating(orderId); setError('');
    const { error } = await supabase.from('orders_table').update({ status: newStatus }).eq('id', orderId);
    if (error) setError('Update failed: ' + error.message);
    else {
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
      showToast(`✓ Order #${String(orderId).padStart(5, '0')} → ${getStatus(newStatus).label}`);
    }
    setUpdating(null);
  }
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3500); }

  const counts   = {};
  ORDER_STATUSES.forEach((s) => { counts[s.key] = orders.filter((o) => (o.status || 'pending') === s.key).length; });
  const filtered = filter === 'all' ? orders : orders.filter((o) => (o.status || 'pending') === filter);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: '1rem' }}>
        {[{ label: 'Total orders', value: orders.length }, { label: 'Pending', value: counts.pending || 0 }, { label: 'Delivered', value: counts.delivered || 0 }]
          .map(({ label, value }) => (
          <div key={label} style={S.statCard}><div style={S.statValue}>{value}</div><div style={S.statLabel}>{label}</div></div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '1rem' }}>
        {[{ key: 'all', label: `All (${orders.length})` }, ...ORDER_STATUSES.map((s) => ({ key: s.key, label: `${s.label} (${counts[s.key] || 0})` }))]
          .map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 20, border: '0.5px solid #ddd', cursor: 'pointer', background: filter === f.key ? '#1a1a1a' : '#fff', color: filter === f.key ? '#fff' : '#555', fontWeight: filter === f.key ? 600 : 400 }}>
            {f.label}
          </button>
        ))}
      </div>
      {error && <div style={S.errorBanner}>⚠️ {error}</div>}
      {loading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>Loading orders...</div>}
      {!loading && filtered.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>No {filter !== 'all' ? filter : ''} orders yet.</div>}
      {!loading && filtered.map((order) => <OrderCard key={order.id} order={order} onStatusChange={handleStatusChange} updating={updating} />)}
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  SUBSCRIPTIONS TAB
// ════════════════════════════════════════════════════════════

function SubStatusBadge({ statusKey }) {
  const s = getSubStatus(statusKey);
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

// ── Dispatch reminder alert strip ─────────────────────────────
function DispatchAlerts({ subs }) {
  const alerts = subs
    .filter((s) => s.status === 'active' && s.next_dispatch)
    .map((s) => ({ ...s, days: daysUntil(s.next_dispatch) }))
    .filter((s) => s.days !== null && s.days <= 7)
    .sort((a, b) => a.days - b.days);

  if (alerts.length === 0) return null;

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '12px 14px', marginBottom: '1rem' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
        🔔 Dispatch Reminders — {alerts.length} subscription{alerts.length !== 1 ? 's' : ''} due soon
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {alerts.map((s) => {
          const plan = PLAN_META[s.plan] || { icon: '📦', label: s.plan };
          const days = s.days;
          return (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 8, padding: '8px 10px', border: '0.5px solid #fcd34d', flexWrap: 'wrap', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>{plan.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{s.user_name || 'Customer'}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{plan.label} · {FREQ_LABEL[s.frequency] || s.frequency}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#666' }}>📅 {fmtDateOnly(s.next_dispatch)}</span>
                <DispatchBadge dateStr={s.next_dispatch} />
                <button
                  onClick={() => {
                    const msg = encodeURIComponent(
                      `Hi ${s.user_name || 'there'}! 👋\n\nThis is a reminder from RK Farm Foods 🌾\n\n` +
                      `Your ${plan.label} subscription is being prepared for dispatch.\n` +
                      `📅 Dispatch Date: ${fmtDateOnly(s.next_dispatch)}\n` +
                      `🔁 Frequency: ${FREQ_LABEL[s.frequency] || s.frequency}\n` +
                      `💰 Amount: ₹${s.price}/month\n\n` +
                      `Please ensure your delivery address is up to date. Thank you! 🙏`
                    );
                    window.open(`https://wa.me/${s.user_phone?.replace(/\D/g, '')}?text=${msg}`, '_blank');
                  }}
                  style={{ ...S.btnGhost, padding: '4px 10px', fontSize: 11, color: '#15803d', borderColor: '#b2dfdb', background: '#f0fff4', whiteSpace: 'nowrap' }}
                >
                  💬 Remind
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Single subscription card ───────────────────────────────────
function SubCard({ sub, onStatusChange, onDispatchChange, onNoteChange, updating }) {
  const [expanded, setExpanded]         = useState(false);
  const [selStatus, setSelStatus]       = useState(sub.status || 'pending');
  const [dispatchDate, setDispatchDate] = useState(sub.next_dispatch ? String(sub.next_dispatch).slice(0, 10) : '');
  const [note, setNote]                 = useState(sub.notes || '');
  const [editNote, setEditNote]         = useState(false);
  const isUpdating = updating === sub.id;
  const plan       = PLAN_META[sub.plan] || { icon: '📦', label: sub.plan, color: '#555' };

  return (
    <div style={{ ...S.card, marginBottom: 10, borderLeft: sub.status === 'active' ? '3px solid #1D9E75' : sub.status === 'pending' ? '3px solid #EF9F27' : 'none' }}>
      {/* Card header */}
      <div style={{ ...S.cardHead, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <div style={{ fontSize: 26, width: 36, textAlign: 'center', flexShrink: 0 }}>{plan.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>
              {sub.user_name || sub.user_email || 'Unknown'}
            </span>
            <SubStatusBadge statusKey={sub.status || 'pending'} />
          </div>
          <div style={S.cardMeta}>
            {plan.label} · {FREQ_LABEL[sub.frequency] || sub.frequency} · <strong style={{ color: config.colors.primary }}>₹{sub.price}/mo</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            {sub.next_dispatch && <DispatchBadge dateStr={sub.next_dispatch} />}
            {sub.next_dispatch && <span style={{ fontSize: 10, color: '#bbb' }}>📅 {fmtDateOnly(sub.next_dispatch)}</span>}
          </div>
        </div>
        <span style={{ ...S.chevron, transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
      </div>

      {expanded && (
        <div style={S.cardBody}>

          {/* Customer info */}
          <div style={{ background: '#f9f8f6', borderRadius: 10, padding: '12px 14px' }}>
            <div style={S.sectionLabel}>👤 Customer details</div>
            <InfoRow label="Name"      value={sub.user_name  || '—'} />
            <InfoRow label="Email"     value={sub.user_email || '—'} />
            <InfoRow label="Phone"     value={sub.user_phone || '—'} />
            <InfoRow label="Subscribed" value={fmtDate(sub.created_at)} />
          </div>

          {/* Plan summary */}
          <div style={{ background: '#f9f8f6', borderRadius: 10, padding: '12px 14px' }}>
            <div style={S.sectionLabel}>📦 Subscription details</div>
            <InfoRow label="Plan"      value={`${plan.icon} ${plan.label}`} />
            <InfoRow label="Frequency" value={FREQ_LABEL[sub.frequency] || sub.frequency} />
            <InfoRow label="Price"     value={`₹${sub.price}/month`} bold />
          </div>

          {/* Next dispatch date picker */}
          {(() => {
            const isCancelled = selStatus === 'cancelled' || sub.status === 'cancelled';
            return (
              <div style={{
                background: isCancelled ? '#fef2f2' : '#f9f8f6',
                border: `0.5px solid ${isCancelled ? '#fecaca' : '#e4e2dc'}`,
                borderRadius: 10, padding: '12px 14px',
                opacity: isCancelled ? 0.7 : 1,
                transition: 'all 0.2s',
              }}>
                <div style={S.sectionLabel}>📅 Next dispatch date</div>
                {isCancelled ? (
                  <div style={{
                    fontSize: 12, color: '#dc2626', fontWeight: 600,
                    background: '#fff', borderRadius: 8, padding: '9px 12px',
                    border: '0.5px solid #fecaca',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    ❌ Dispatch date disabled — subscription is cancelled
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="date"
                        value={dispatchDate}
                        onChange={(e) => setDispatchDate(e.target.value)}
                        disabled={isCancelled}
                        style={{
                          flex: 1, minWidth: 140, padding: '8px 10px', fontSize: 13,
                          fontFamily: 'inherit', borderRadius: 8, border: '0.5px solid #ddd',
                          background: '#fff', color: '#1a1a1a', outline: 'none',
                          cursor: isCancelled ? 'not-allowed' : 'text',
                        }}
                      />
                      <button
                        onClick={() => { if (dispatchDate) onDispatchChange(sub.id, dispatchDate.slice(0, 10)); }}
                        disabled={isUpdating || !dispatchDate || isCancelled}
                        style={{ ...S.btnPrimary, padding: '8px 14px', fontSize: 12, opacity: (!dispatchDate || isUpdating || isCancelled) ? 0.45 : 1 }}>
                        {isUpdating ? 'Saving...' : 'Set Date →'}
                      </button>
                    </div>
                    {sub.next_dispatch && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                        Currently set: <strong>{fmtDateOnly(sub.next_dispatch)}</strong>
                        &nbsp;·&nbsp;<DispatchBadge dateStr={sub.next_dispatch} />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Status updater */}
          <div style={{ background: '#f9f8f6', border: '0.5px solid #e4e2dc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={S.sectionLabel}>Update subscription status</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={selStatus} onChange={(e) => setSelStatus(e.target.value)}
                style={{ flex: 1, minWidth: 130, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', color: '#1a1a1a', outline: 'none' }}>
                {SUB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button
                onClick={() => {
                  if (selStatus !== sub.status) {
                    onStatusChange(sub.id, selStatus);
                    if (selStatus === 'cancelled') {
                      onDispatchChange(sub.id, null);
                      setDispatchDate('');
                    }
                  }
                }}
                disabled={isUpdating || selStatus === sub.status}
                style={{ ...S.btnPrimary, padding: '8px 14px', opacity: (isUpdating || selStatus === sub.status) ? 0.45 : 1, cursor: (isUpdating || selStatus === sub.status) ? 'not-allowed' : 'pointer' }}>
                {isUpdating ? 'Saving...' : 'Update →'}
              </button>
            </div>
            {/* Quick actions */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {sub.status === 'pending' && (
                <button onClick={() => onStatusChange(sub.id, 'active')} disabled={isUpdating}
                  style={{ ...S.btnPrimary, padding: '6px 12px', fontSize: 12, background: '#15803d' }}>
                  ✅ Activate
                </button>
              )}
              {sub.status === 'active' && (
                <button onClick={() => onStatusChange(sub.id, 'paused')} disabled={isUpdating}
                  style={{ ...S.btnGhost, padding: '6px 12px', fontSize: 12, color: '#1d4ed8', borderColor: '#bfdbfe' }}>
                  ⏸ Pause
                </button>
              )}
              {sub.status === 'paused' && (
                <button onClick={() => onStatusChange(sub.id, 'active')} disabled={isUpdating}
                  style={{ ...S.btnPrimary, padding: '6px 12px', fontSize: 12, background: '#15803d' }}>
                  ▶ Resume
                </button>
              )}
              {sub.status !== 'cancelled' && (
                <button onClick={() => {
                  onStatusChange(sub.id, 'cancelled');
                  onDispatchChange(sub.id, null);
                  setDispatchDate('');
                  setSelStatus('cancelled');
                }} disabled={isUpdating}
                  style={{ ...S.btnGhost, padding: '6px 12px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }}>
                  ❌ Cancel
                </button>
              )}
            </div>
          </div>

          {/* Admin notes */}
          <div style={{ background: '#f9f8f6', border: '0.5px solid #e4e2dc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={S.sectionLabel}>📝 Admin notes</div>
              <button onClick={() => setEditNote((v) => !v)}
                style={{ ...S.btnGhost, padding: '3px 10px', fontSize: 11 }}>
                {editNote ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {editNote ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="Add internal notes about this subscription..."
                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', color: '#1a1a1a', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                <button
                  onClick={() => { onNoteChange(sub.id, note); setEditNote(false); }}
                  disabled={isUpdating}
                  style={{ ...S.btnPrimary, alignSelf: 'flex-end', padding: '7px 14px', fontSize: 12 }}>
                  Save Note →
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: sub.notes ? '#555' : '#bbb', fontStyle: sub.notes ? 'normal' : 'italic', lineHeight: 1.6 }}>
                {sub.notes || 'No notes yet.'}
              </div>
            )}
          </div>

          {/* WhatsApp actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const plan = PLAN_META[sub.plan] || { icon: '📦', label: sub.plan };
                const msg  = encodeURIComponent(
                  `Hi ${sub.user_name || 'there'}! 👋\n\nThank you for subscribing to RK Farm Foods 🌾\n\n` +
                  `✅ Your ${plan.label} subscription is now ${getSubStatus(sub.status || 'pending').label}.\n` +
                  `🔁 Frequency: ${FREQ_LABEL[sub.frequency] || sub.frequency}\n` +
                  `💰 Price: ₹${sub.price}/month\n` +
                  (sub.next_dispatch ? `📅 Next Dispatch: ${fmtDateOnly(sub.next_dispatch)}\n` : '') +
                  `\nWe'll notify you before every dispatch. Thank you! 🙏`
                );
                window.open(`https://wa.me/${sub.user_phone?.replace(/\D/g, '')}?text=${msg}`, '_blank');
              }}
              style={{ ...S.btnGhost, flex: 1, background: '#f0fff4', borderColor: '#b2dfdb', color: '#15803d', fontSize: 12 }}>
              💬 Status Update
            </button>
            {sub.next_dispatch && (
              <button
                onClick={() => {
                  const plan = PLAN_META[sub.plan] || { icon: '📦', label: sub.plan };
                  const msg  = encodeURIComponent(
                    `Hi ${sub.user_name || 'there'}! 🌾\n\n` +
                    `Your ${plan.label} subscription box is being packed and will be dispatched on ${fmtDateOnly(sub.next_dispatch)}.\n\n` +
                    `📦 Please ensure someone is available to receive it.\n` +
                    `📍 If your address has changed, reply here immediately.\n\n` +
                    `Thank you for subscribing to RK Farm Foods! 🙏`
                  );
                  window.open(`https://wa.me/${sub.user_phone?.replace(/\D/g, '')}?text=${msg}`, '_blank');
                }}
                style={{ ...S.btnGhost, flex: 1, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', fontSize: 12 }}>
                📦 Dispatch Alert
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 13, paddingBottom: 6, marginBottom: 6, borderBottom: '0.5px solid #f0ede6', gap: 10 }}>
      <span style={{ color: '#999', flexShrink: 0, fontSize: 12 }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 500, color: '#1a1a1a', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

function SubscriptionsTab() {
  const [subs, setSubs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter]     = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [toast, setToast]       = useState(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    loadSubs();
    const channel = supabase
      .channel('admin_subs_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subscriptions' }, loadSubs)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  async function loadSubs() {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('id, user_id, user_name, user_email, user_phone, plan, frequency, price, status, next_dispatch, notes, created_at')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) setError('Failed to load subscriptions: ' + error.message);
    else setSubs(data || []);
    setLoading(false);
  }

  async function handleStatusChange(subId, newStatus) {
    setUpdating(subId); setError('');
    const { error } = await supabase.from('subscriptions').update({ status: newStatus }).eq('id', subId);
    if (error) setError('Update failed: ' + error.message);
    else {
      setSubs((prev) => prev.map((s) => s.id === subId ? { ...s, status: newStatus } : s));
      showToast(`✓ Subscription #${subId} → ${getSubStatus(newStatus).label}`);
    }
    setUpdating(null);
  }

  async function handleDispatchChange(subId, date) {
    const cleanDate = date ? String(date).slice(0, 10) : null;
    setUpdating(subId); setError('');
    const { error } = await supabase.from('subscriptions').update({ next_dispatch: cleanDate }).eq('id', subId);
    if (error) setError('Update failed: ' + error.message);
    else {
      setSubs((prev) => prev.map((s) => s.id === subId ? { ...s, next_dispatch: cleanDate } : s));
      if (cleanDate) showToast(`✓ Dispatch date set to ${fmtDateOnly(cleanDate)}`);
      else showToast('✓ Dispatch date cleared');
    }
    setUpdating(null);
  }

  async function handleNoteChange(subId, notes) {
    setUpdating(subId); setError('');
    const { error } = await supabase.from('subscriptions').update({ notes }).eq('id', subId);
    if (error) setError('Update failed: ' + error.message);
    else {
      setSubs((prev) => prev.map((s) => s.id === subId ? { ...s, notes } : s));
      showToast('✓ Note saved');
    }
    setUpdating(null);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3500); }

  const counts = {};
  SUB_STATUSES.forEach((s) => { counts[s.key] = subs.filter((o) => (o.status || 'pending') === s.key).length; });
  const dispatchSoon = subs.filter((s) => s.status === 'active' && s.next_dispatch && daysUntil(s.next_dispatch) <= 7 && daysUntil(s.next_dispatch) !== null).length;
  const monthlyRevenue = subs.filter((s) => s.status === 'active').reduce((sum, s) => sum + (s.price || 0), 0);

  let filtered = filter === 'all' ? subs : subs.filter((s) => (s.status || 'pending') === filter);
  if (planFilter !== 'all') filtered = filtered.filter((s) => s.plan === planFilter);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: '1rem' }}>
        {[
          { label: 'Total subscriptions', value: subs.length },
          { label: 'Active',              value: counts.active || 0 },
          { label: 'Monthly revenue',     value: `₹${monthlyRevenue.toLocaleString('en-IN')}` },
          { label: 'Dispatch this week',  value: dispatchSoon },
        ].map(({ label, value }) => (
          <div key={label} style={S.statCard}>
            <div style={S.statValue}>{value}</div>
            <div style={S.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <DispatchAlerts subs={subs} />

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 8 }}>
        {[{ key: 'all', label: `All (${subs.length})` }, ...SUB_STATUSES.map((s) => ({ key: s.key, label: `${s.label} (${counts[s.key] || 0})` }))]
          .map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 20, border: '0.5px solid #ddd', cursor: 'pointer', background: filter === f.key ? '#1a1a1a' : '#fff', color: filter === f.key ? '#fff' : '#555', fontWeight: filter === f.key ? 600 : 400 }}>
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '1rem' }}>
        {[{ key: 'all', label: 'All Plans' }, ...Object.entries(PLAN_META).map(([k, v]) => ({ key: k, label: `${v.icon} ${v.label}` }))]
          .map((f) => (
          <button key={f.key} onClick={() => setPlanFilter(f.key)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 20, border: `0.5px solid ${planFilter === f.key ? config.colors.primary : '#ddd'}`, cursor: 'pointer', background: planFilter === f.key ? config.colors.primary : '#fff', color: planFilter === f.key ? '#fff' : '#555', fontWeight: planFilter === f.key ? 600 : 400 }}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <div style={S.errorBanner}>⚠️ {error}</div>}
      {loading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>Loading subscriptions...</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>
          No subscriptions yet.
        </div>
      )}
      {!loading && filtered.map((sub) => (
        <SubCard
          key={sub.id}
          sub={sub}
          onStatusChange={handleStatusChange}
          onDispatchChange={handleDispatchChange}
          onNoteChange={handleNoteChange}
          updating={updating}
        />
      ))}
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ── Default subscription base prices (fallback if DB empty) ──
const DEFAULT_SUB_PRICES = { starter: 599, family: 1199, wellness: 1999 };
const SUB_PLAN_META_PRICES = [
  { key: 'starter',  icon: '🌾', name: 'Starter Box',  desc: 'Individual / couple plan' },
  { key: 'family',   icon: '🏡', name: 'Family Box',   desc: 'Household of 3–5'         },
  { key: 'wellness', icon: '✨', name: 'Wellness Box', desc: 'Complete organic pantry'   },
];

function SubscriptionPricesSection() {
  const [prices, setPrices]   = useState({ starter: '', family: '', wellness: '' });
  const [saved,  setSaved]    = useState({ starter: 0, family: 0, wellness: 0 });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [error,   setError]   = useState('');

  useEffect(() => { loadPrices(); }, []);

  async function loadPrices() {
    setLoading(true);
    const { data, error } = await supabase
      .from('subscription_prices')
      .select('plan, price');
    if (error) { setError('Failed to load subscription prices: ' + error.message); setLoading(false); return; }
    const map = { ...DEFAULT_SUB_PRICES };
    (data || []).forEach((r) => { map[r.plan] = r.price; });
    setPrices({ starter: map.starter, family: map.family, wellness: map.wellness });
    setSaved({ starter: map.starter, family: map.family, wellness: map.wellness });
    setLoading(false);
  }

  const isDirtyPrice = (key) => Number(prices[key]) !== Number(saved[key]);
  const anyDirty = SUB_PLAN_META_PRICES.some((p) => isDirtyPrice(p.key));

  async function saveSubPrices() {
    const dirtyPlans = SUB_PLAN_META_PRICES.filter((p) => isDirtyPrice(p.key));
    if (!dirtyPlans.length) return;
    setSaving(true); setError('');
    try {
      await Promise.all(dirtyPlans.map((p) =>
        supabase.from('subscription_prices').upsert(
          { plan: p.key, price: Number(prices[p.key]) },
          { onConflict: 'plan' }
        )
      ));
      await loadPrices();
      showToast(`✓ ${dirtyPlans.length} subscription price${dirtyPlans.length !== 1 ? 's' : ''} saved`);
    } catch (err) { setError('Save failed: ' + err.message); }
    finally { setSaving(false); }
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>📦 Subscription Plan Prices</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Base monthly prices — discounts applied automatically at checkout</div>
        </div>
        {anyDirty && (
          <button style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1, fontSize: 12, padding: '7px 14px' }} onClick={saveSubPrices} disabled={saving}>
            {saving ? 'Saving...' : `Save changes →`}
          </button>
        )}
      </div>

      {error && <div style={S.errorBanner}>⚠️ {error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#aaa', fontSize: 13 }}>Loading subscription prices...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SUB_PLAN_META_PRICES.map((plan) => {
            const dirty = isDirtyPrice(plan.key);
            return (
              <div key={plan.key} style={{
                ...S.card,
                ...(dirty ? S.cardDirty : Number(prices[plan.key]) !== DEFAULT_SUB_PRICES[plan.key] ? S.cardSaved : {}),
              }}>
                <div style={{ ...S.cardHead }}>
                  <span style={{ ...S.cardEmoji }}>{plan.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.cardName}>{plan.name}</div>
                    <div style={S.cardMeta}>{plan.desc}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {dirty && <span style={S.badge('amber')}>Unsaved</span>}
                    {!dirty && Number(prices[plan.key]) !== DEFAULT_SUB_PRICES[plan.key] && <span style={S.badge('teal')}>Live ✓</span>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={S.rupee}>₹</span>
                      <input
                        type="number" min="0"
                        value={prices[plan.key]}
                        onChange={(e) => {
                          setError('');
                          setPrices((prev) => ({ ...prev, [plan.key]: e.target.value }));
                        }}
                        style={{ ...S.priceInput, width: 110, ...(dirty ? S.priceInputChanged : {}) }}
                      />
                    </div>
                    {dirty && <span style={S.wasBadge}>was ₹{saved[plan.key]}</span>}
                  </div>
                </div>
                <div style={{ padding: '0 16px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Monthly',        discount: 0  },
                    { label: 'Every 3 Months', discount: 8  },
                    { label: 'Every 6 Months', discount: 17 },
                  ].map((f) => {
                    const discounted = Math.round(Number(prices[plan.key]) * (1 - f.discount / 100));
                    return (
                      <div key={f.label} style={{ background: '#f9f8f6', borderRadius: 8, padding: '5px 10px', fontSize: 11, border: '0.5px solid #e4e2dc' }}>
                        <span style={{ color: '#999' }}>{f.label}: </span>
                        <strong style={{ color: config.colors.primary }}>₹{discounted}/mo</strong>
                        {f.discount > 0 && <span style={{ color: '#15803d', marginLeft: 4 }}>(-{f.discount}%)</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {toast && <div style={S.toast}>{toast}</div>}

      <div style={{ borderTop: '0.5px solid #e4e2dc', margin: '1.5rem 0 0', paddingTop: '1rem' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>🛒 Product Prices</div>
        <div style={{ fontSize: 12, color: '#999', marginBottom: '1rem' }}>Individual product prices shown in the shop</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  PRICE MANAGER TAB
// ════════════════════════════════════════════════════════════
function PriceRow({ size, value, originalValue, savedValue, onChange }) {
  const isChanged = savedValue != null ? Number(value) !== Number(savedValue) : Number(value) !== Number(originalValue);
  return (
    <div style={S.priceRow}>
      <span style={S.sizeLabel}>{size}</span>
      <div style={S.inputWrap}>
        <span style={S.rupee}>₹</span>
        <input type="number" min="0" value={value} onChange={(e) => onChange(size, e.target.value)}
          style={{ ...S.priceInput, ...(isChanged ? S.priceInputChanged : {}) }} />
        {isChanged && <span style={S.wasBadge}>was ₹{savedValue != null ? savedValue : originalValue}</span>}
      </div>
    </div>
  );
}

function PriceCard({ draft, onChange, onToggle, onReset }) {
  const dirty      = isDirty(draft);
  const overridden = !!draft.savedPrices;
  return (
    <div style={{ ...S.card, ...(dirty ? S.cardDirty : overridden ? S.cardSaved : {}) }}>
      <div style={S.cardHead} onClick={onToggle}>
        <span style={S.cardEmoji}>{draft.emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.cardName}>{draft.name}</div>
          <div style={S.cardMeta}>{draft.category} &nbsp;·&nbsp; {Object.entries(draft.prices).map(([s, v]) => `${s}: ₹${v}`).join('  |  ')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {dirty      && <span style={S.badge('amber')}>Unsaved</span>}
          {overridden && !dirty && <span style={S.badge('teal')}>Live ✓</span>}
          <span style={{ ...S.chevron, transform: draft.expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        </div>
      </div>
      {draft.expanded && (
        <div style={S.cardBody}>
          {draft.sizes.map((size) => (
            <PriceRow key={size} size={size} value={draft.prices[size]} originalValue={draft.originalPrices[size]} savedValue={draft.savedPrices?.[size] ?? null} onChange={onChange} />
          ))}
          {overridden && (
            <button style={{ ...S.btnGhost, marginTop: 6, fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }} onClick={onReset}>
              Reset to default price
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PricesTab() {
  const [drafts, setDrafts]             = useState([]);
  const [overrides, setOverrides]       = useState({});
  const [loadingData, setLoadingData]   = useState(true);
  const [saving, setSaving]             = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [toast, setToast]               = useState(null);
  const [error, setError]               = useState('');

  useEffect(() => {
    fetchOverrides()
      .then((data) => { setOverrides(data); setDrafts(buildDraft(data)); })
      .catch((err) => setError('Failed to load prices: ' + err.message))
      .finally(() => setLoadingData(false));
  }, []);

  function handlePriceChange(pid, size, value) { setDrafts((prev) => prev.map((d) => d.id === pid ? { ...d, prices: { ...d.prices, [size]: value } } : d)); }
  function toggleExpand(pid) { setDrafts((prev) => prev.map((d) => d.id === pid ? { ...d, expanded: !d.expanded } : d)); }

  async function saveAll() {
    const dirtyDrafts = drafts.filter(isDirty);
    if (!dirtyDrafts.length) return;
    setSaving(true); setError('');
    try {
      await Promise.all(dirtyDrafts.map((d) => saveOverride(d.id, Object.fromEntries(Object.entries(d.prices).map(([s, v]) => [s, Number(v)])))));
      const fresh = await fetchOverrides();
      setOverrides(fresh); setDrafts(buildDraft(fresh));
      showToast(`✓ ${dirtyDrafts.length} product${dirtyDrafts.length !== 1 ? 's' : ''} saved`);
    } catch (err) { setError('Save failed: ' + err.message); }
    finally { setSaving(false); }
  }
  async function resetProduct(pid) {
    setSaving(true);
    try {
      await deleteOverride(pid);
      const fresh = await fetchOverrides();
      setOverrides(fresh); setDrafts(buildDraft(fresh));
      showToast('✓ Product reset to default');
    } catch (err) { setError('Reset failed: ' + err.message); }
    finally { setSaving(false); }
  }
  async function doResetAll() {
    setSaving(true);
    try {
      await resetAllOverrides();
      setOverrides({}); setDrafts(buildDraft({})); setConfirmReset(false);
      showToast('✓ All prices reset to defaults');
    } catch (err) { setError('Reset failed: ' + err.message); }
    finally { setSaving(false); }
  }
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  const dirtyCount    = drafts.filter(isDirty).length;
  const overrideCount = Object.keys(overrides).length;

  return (
    <div>
      <SubscriptionPricesSection />

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {overrideCount > 0 && !saving && <button style={S.btnGhost} onClick={() => setConfirmReset(true)}>Reset all</button>}
        {dirtyCount > 0 && (
          <button style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={saveAll} disabled={saving}>
            {saving ? 'Saving...' : `Save ${dirtyCount} change${dirtyCount !== 1 ? 's' : ''} →`}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: '1rem' }}>
        {[{ label: 'Total products', value: baseProducts.length }, { label: 'Custom prices', value: overrideCount }, { label: 'Unsaved edits', value: dirtyCount }]
          .map(({ label, value }) => (
          <div key={label} style={S.statCard}><div style={S.statValue}>{value}</div><div style={S.statLabel}>{label}</div></div>
        ))}
      </div>
      {error && <div style={S.errorBanner}>⚠️ {error}</div>}
      {loadingData
        ? <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>Loading prices...</div>
        : <div style={S.list}>{drafts.map((draft) => (
            <PriceCard key={draft.id} draft={draft}
              onChange={(size, val) => handlePriceChange(draft.id, size, val)}
              onToggle={() => toggleExpand(draft.id)}
              onReset={() => resetProduct(draft.id)}
            />
          ))}</div>
      }
      {confirmReset && (
        <div style={S.modalBackdrop}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>Reset all prices?</h3>
            <p style={S.modalSub}>All {overrideCount} custom prices will be removed from the database.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={S.btnGhost} onClick={() => setConfirmReset(false)}>Cancel</button>
              <button style={{ ...S.btnPrimary, background: '#E24B4A' }} onClick={doResetAll} disabled={saving}>
                {saving ? 'Resetting...' : 'Reset all'}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  STOCK MANAGEMENT TAB  ← NEW
// ════════════════════════════════════════════════════════════
function StockTab() {
  const [stockMap, setStockMap]   = useState({});   // { productId: { status, quantity, notes, updated_at } }
  const [drafts, setDrafts]       = useState({});   // unsaved local edits
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);
  const [error, setError]         = useState('');
  const [expanded, setExpanded]   = useState({});   // { productId: bool }
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadStock();
    const ch = supabase
      .channel('admin_stock_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_stock' }, loadStock)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, []);

  async function loadStock() {
    const { data, error } = await supabase.from('product_stock').select('*');
    if (error) { setError('Failed to load stock: ' + error.message); setLoading(false); return; }
    const map = {};
    (data || []).forEach((r) => { map[String(r.product_id)] = r; });
    setStockMap(map);
    setDrafts({});
    setLoading(false);
  }

  // Read a field: draft value takes priority over saved, then defaults
  function getField(productId, field) {
    const pid = String(productId);
    if (drafts[pid]?.[field] !== undefined) return drafts[pid][field];
    if (stockMap[pid]?.[field] !== undefined) return stockMap[pid][field];
    return field === 'status' ? 'in_stock' : '';
  }

  function setField(productId, field, value) {
    const pid = String(productId);
    setDrafts((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], [field]: value },
    }));
  }

  function isProductDirty(productId) {
    const pid = String(productId);
    const d   = drafts[pid];
    if (!d) return false;
    const saved = stockMap[pid];
    return Object.keys(d).some((k) => {
      const savedVal = saved?.[k] ?? (k === 'status' ? 'in_stock' : '');
      return String(d[k]) !== String(savedVal ?? '');
    });
  }

  const dirtyIds = baseProducts.map((p) => String(p.id)).filter(isProductDirty);

  async function saveSingle(productId) {
    const pid     = String(productId);
    const current = stockMap[pid]  || {};
    const patch   = drafts[pid]    || {};
    setSaving(true); setError('');
    const { error } = await supabase.from('product_stock').upsert(
      {
        product_id: pid,
        status:     patch.status   !== undefined ? patch.status   : (current.status   ?? 'in_stock'),
        quantity:   patch.quantity !== undefined ? (Number(patch.quantity) || null)    : (current.quantity ?? null),
        notes:      patch.notes    !== undefined ? patch.notes    : (current.notes    ?? ''),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'product_id' }
    );
    if (error) setError('Save failed: ' + error.message);
    else { await loadStock(); showToast('✓ Stock updated'); }
    setSaving(false);
  }

  async function saveAll() {
    if (!dirtyIds.length) return;
    setSaving(true); setError('');
    try {
      await Promise.all(
        dirtyIds.map((pid) => {
          const current = stockMap[pid] || {};
          const patch   = drafts[pid]  || {};
          return supabase.from('product_stock').upsert(
            {
              product_id: pid,
              status:     patch.status   !== undefined ? patch.status   : (current.status   ?? 'in_stock'),
              quantity:   patch.quantity !== undefined ? (Number(patch.quantity) || null)    : (current.quantity ?? null),
              notes:      patch.notes    !== undefined ? patch.notes    : (current.notes    ?? ''),
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'product_id' }
          );
        })
      );
      await loadStock();
      showToast(`✓ ${dirtyIds.length} product${dirtyIds.length !== 1 ? 's' : ''} updated`);
    } catch (err) { setError('Save failed: ' + err.message); }
    finally { setSaving(false); }
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  // Stats
  const stockCounts = { in_stock: 0, low_stock: 0, out_of_stock: 0 };
  baseProducts.forEach((p) => {
    const st = stockMap[String(p.id)]?.status ?? 'in_stock';
    if (stockCounts[st] !== undefined) stockCounts[st]++;
  });

  // Filtered product list
  const filteredProducts = statusFilter === 'all'
    ? baseProducts
    : baseProducts.filter((p) => (stockMap[String(p.id)]?.status ?? 'in_stock') === statusFilter);

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: '1rem' }}>
        {STOCK_STATUSES.map(({ key, label }) => (
          <div key={key} style={S.statCard}>
            <div style={S.statValue}>{stockCounts[key]}</div>
            <div style={S.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '1rem' }}>
        {[{ key: 'all', label: `All (${baseProducts.length})` }, ...STOCK_STATUSES.map((s) => ({ key: s.key, label: `${s.label} (${stockCounts[s.key]})` }))]
          .map((f) => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 20, border: '0.5px solid #ddd', cursor: 'pointer', background: statusFilter === f.key ? '#1a1a1a' : '#fff', color: statusFilter === f.key ? '#fff' : '#555', fontWeight: statusFilter === f.key ? 600 : 400 }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Save all button */}
      {dirtyIds.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={saveAll} disabled={saving}>
            {saving ? 'Saving...' : `Save ${dirtyIds.length} change${dirtyIds.length !== 1 ? 's' : ''} →`}
          </button>
        </div>
      )}

      {error && <div style={S.errorBanner}>⚠️ {error}</div>}

      {loading
        ? <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>Loading stock...</div>
        : (
          <div style={S.list}>
            {filteredProducts.map((product) => {
              const pid    = String(product.id);
              const status = getField(pid, 'status');
              const qty    = getField(pid, 'quantity');
              const notes  = getField(pid, 'notes');
              const dirty  = isProductDirty(pid);
              const saved  = stockMap[pid];
              const exp    = !!expanded[pid];
              const st     = STOCK_STATUSES.find((s) => s.key === status) || STOCK_STATUSES[0];

              return (
                <div key={pid} style={{
                  ...S.card,
                  marginBottom: 0,
                  ...(dirty ? S.cardDirty : saved ? S.cardSaved : {}),
                }}>
                  {/* Card header — always visible */}
                  <div
                    style={{ ...S.cardHead, cursor: 'pointer' }}
                    onClick={() => setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }))}
                  >
                    <span style={S.cardEmoji}>{product.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.cardName}>{product.name}</div>
                      <div style={S.cardMeta}>
                        {product.category}
                        {saved?.quantity != null && !dirty && (
                          <span style={{ marginLeft: 6, color: '#aaa' }}>· {saved.quantity} units</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {dirty && <span style={S.badge('amber')}>Unsaved</span>}
                      <span style={{ background: st.bg, color: st.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                        {st.label}
                      </span>
                      <span style={{ ...S.chevron, transform: exp ? 'rotate(90deg)' : 'none' }}>▶</span>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {exp && (
                    <div style={S.cardBody}>

                      {/* Status selector */}
                      <div>
                        <div style={S.sectionLabel}>Stock status</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {STOCK_STATUSES.map((s) => (
                            <button key={s.key} onClick={() => setField(pid, 'status', s.key)}
                              style={{
                                padding: '7px 14px', fontSize: 12, fontFamily: 'inherit', borderRadius: 20,
                                border: `1.5px solid ${status === s.key ? s.color : '#ddd'}`,
                                background: status === s.key ? s.bg : '#fff',
                                color: status === s.key ? s.color : '#888',
                                fontWeight: status === s.key ? 700 : 400,
                                cursor: 'pointer',
                              }}>
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Quantity */}
                      <div>
                        <div style={S.sectionLabel}>Quantity (optional)</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="number" min="0" placeholder="e.g. 48"
                            value={qty ?? ''}
                            onChange={(e) => setField(pid, 'quantity', e.target.value)}
                            style={{ ...S.priceInput, width: 120 }}
                          />
                          <span style={{ fontSize: 12, color: '#aaa' }}>units</span>
                        </div>
                      </div>

                      {/* Admin notes */}
                      <div>
                        <div style={S.sectionLabel}>Internal notes</div>
                        <textarea
                          rows={2}
                          placeholder="e.g. Restocking next week, supplier delayed…"
                          value={notes ?? ''}
                          onChange={(e) => setField(pid, 'notes', e.target.value)}
                          style={{
                            width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                            borderRadius: 8, border: '0.5px solid #ddd', background: '#fafaf9',
                            color: '#1a1a1a', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Last updated */}
                      {saved?.updated_at && (
                        <div style={{ fontSize: 11, color: '#aaa' }}>
                          Last updated: {fmtDate(saved.updated_at)}
                        </div>
                      )}

                      {/* Per-product save button */}
                      {dirty && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => saveSingle(pid)}
                            disabled={saving}
                            style={{ ...S.btnPrimary, fontSize: 12, padding: '8px 16px', opacity: saving ? 0.7 : 1 }}>
                            {saving ? 'Saving...' : 'Save →'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {filteredProducts.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>
          No products with this stock status.
        </div>
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MAIN ADMIN PANEL
// ════════════════════════════════════════════════════════════
export default function AdminPanel() {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking]   = useState(true);
  const [tab, setTab]             = useState('orders');

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) { setChecking(false); return; }
      const { data } = await supabase.from('admins').select('id').eq('id', session.user.id).maybeSingle();
      if (data) setAdminUser(session.user);
      else await supabase.auth.signOut();
      setChecking(false);
    });
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    setAdminUser(null);
  }

  if (checking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}><div style={{ fontSize: 13, color: '#aaa' }}>Loading...</div></div>;
  }

  if (!adminUser) return <LoginScreen onLogin={(user) => setAdminUser(user)} />;

  const TABS = [
    { key: 'orders',        label: '📋 Orders'        },
    { key: 'subscriptions', label: '📦 Subscriptions' },
    { key: 'prices',        label: '💰 Prices'        },
    { key: 'stock',         label: '🏷️ Stock'          },
  ];

  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <button style={S.backBtn} onClick={() => navigate('/')}>← Store</button>
          <h1 style={S.pageTitle}>Admin Panel</h1>
          <p style={{ fontSize: 12, color: '#aaa', margin: '2px 0 0' }}>{adminUser.email}</p>
        </div>
        <button style={S.btnGhost} onClick={handleLogout}>Sign out</button>
      </div>

      {/* Tab switcher — 4 tabs */}
      <div style={{ display: 'flex', background: '#f9f8f6', borderRadius: 10, padding: 4, marginBottom: '1.5rem', border: '0.5px solid #e4e2dc' }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: '9px 0', fontSize: 12, fontWeight: tab === t.key ? 600 : 400,
            fontFamily: 'inherit', border: 'none', cursor: 'pointer', borderRadius: 8,
            background: tab === t.key ? '#fff' : 'transparent',
            color:      tab === t.key ? '#1a1a1a' : '#888',
            boxShadow:  tab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'orders'        && <OrdersTab />}
      {tab === 'subscriptions' && <SubscriptionsTab />}
      {tab === 'prices'        && <PricesTab />}
      {tab === 'stock'         && <StockTab />}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const S = {
  page:        { maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem 4rem', fontFamily: "'DM Sans','Nunito',sans-serif" },
  backBtn:     { background: 'none', border: 'none', color: '#aaa', fontSize: 12, cursor: 'pointer', padding: '0 0 2px', fontFamily: 'inherit', display: 'block' },
  pageTitle:   { fontSize: 24, fontWeight: 600, color: '#1a1a1a', letterSpacing: '-0.5px', margin: 0 },
  statCard:    { background: '#f9f8f6', borderRadius: 10, padding: '14px 16px', border: '0.5px solid #e8e6e0' },
  statValue:   { fontSize: 26, fontWeight: 600, color: '#1a1a1a', lineHeight: 1 },
  statLabel:   { fontSize: 12, color: '#999', marginTop: 4 },
  list:        { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '1.5rem' },
  card:        { background: '#fff', border: '0.5px solid #e4e2dc', borderRadius: 12, overflow: 'hidden' },
  cardDirty:   { borderLeft: '3px solid #EF9F27' },
  cardSaved:   { borderLeft: '3px solid #1D9E75' },
  cardHead:    { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px' },
  cardEmoji:   { fontSize: 22, width: 34, textAlign: 'center', flexShrink: 0 },
  cardName:    { fontSize: 14, fontWeight: 600, color: '#1a1a1a' },
  cardMeta:    { fontSize: 12, color: '#999', marginTop: 2 },
  chevron:     { fontSize: 11, color: '#bbb', transition: 'transform 0.2s', flexShrink: 0 },
  cardBody:    { borderTop: '0.5px solid #f0ede6', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  sectionLabel:{ fontSize: 10, color: '#bbb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  priceRow:    { display: 'flex', alignItems: 'center', gap: 12 },
  sizeLabel:   { width: 48, fontSize: 13, color: '#888', fontWeight: 500, flexShrink: 0 },
  inputWrap:   { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  rupee:       { fontSize: 14, color: '#555', fontWeight: 500 },
  priceInput:  { width: 100, padding: '7px 10px', fontSize: 15, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", border: '0.5px solid #ddd', borderRadius: 8, background: '#fafaf9', color: '#1a1a1a', outline: 'none' },
  priceInputChanged: { borderColor: '#EF9F27', background: '#FAEEDA' },
  wasBadge:    { fontSize: 12, color: '#aaa', textDecoration: 'line-through' },
  badge:       (c) => ({ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500, ...(c === 'amber' ? { background: '#FAEEDA', color: '#BA7517' } : { background: '#E1F5EE', color: '#0F6E56' }) }),
  errorBanner: { background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: '1rem' },
  btnPrimary:  { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' },
  btnGhost:    { background: 'transparent', color: '#555', border: '0.5px solid #ddd', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' },
  loginWrap:   { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f8f6', fontFamily: "'DM Sans',sans-serif", padding: '1rem' },
  loginCard:   { background: '#fff', border: '0.5px solid #e4e2dc', borderRadius: 20, padding: '2.5rem 2rem', width: '100%', maxWidth: 380 },
  loginTitle:  { fontSize: 22, fontWeight: 700, color: '#1a1a1a', margin: 0 },
  loginSub:    { fontSize: 13, color: '#aaa', margin: '4px 0 0' },
  fieldWrap:   { marginBottom: 14 },
  fieldLabel:  { fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginBottom: 5 },
  input:       { width: '100%', padding: '11px 14px', fontSize: 15, fontFamily: "'DM Sans',sans-serif", border: '0.5px solid #ddd', borderRadius: 8, background: '#fafaf9', color: '#1a1a1a', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' },
  loginError:  { background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#dc2626', marginBottom: 14 },
  loginBtn:    { width: '100%', padding: '12px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: 'pointer', marginTop: 4 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:       { background: '#fff', borderRadius: 14, padding: '1.5rem', maxWidth: 380, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
  modalTitle:  { fontSize: 17, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 },
  modalSub:    { fontSize: 14, color: '#888', lineHeight: 1.5 },
  toast:       { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a1a', color: '#fff', padding: '10px 20px', borderRadius: 30, fontSize: 13, fontWeight: 500, zIndex: 9999, fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap' },
};
